/**
 * Recorder — taps the engine's master output and captures Float32 PCM
 * into memory, then encodes a 16-bit stereo WAV blob for download.
 *
 * Why WAV (and not MediaRecorder/WebM)?
 *   - Ableton (and every other DAW) imports WAV cleanly at full quality.
 *     WebM/Opus is lossy and would need a transcoding step before use.
 *   - 16-bit PCM is the universal lingua franca — nothing more exotic
 *     buys us anything for synth recording.
 *
 * Audio graph during a recording:
 *
 *   engine.master ─► tap (gain=1) ─► script ─► silentSink (gain=0) ─► destination
 *                                       │
 *                                       └── push samples into channels[]
 *
 * The silent sink keeps the script processor's onaudioprocess firing
 * without doubling the audible output (engine.master → destination is
 * still in place, untouched).
 *
 * ScriptProcessorNode is officially deprecated, but it's still in
 * every shipping browser and produces the simplest implementation.
 * For a tap (no realtime mutation, no latency-sensitive feedback),
 * its added latency is moot. AudioWorklet would be cleaner; we can
 * upgrade later without changing the public API of this class.
 */

import type { AudioEngine } from './AudioEngine';

/** Buffer size for the script tap. 4096 is the sweet spot — small
 *  enough that very short recordings still fire a callback, large
 *  enough to keep onaudioprocess overhead trivial. */
const TAP_BUFFER_SIZE = 4096;

/**
 * Auto-stop ceiling. A long recording can balloon main-thread memory
 * (a 16-bit stereo WAV at 48 kHz is ~11.5 MB/min, but the in-flight
 * Float32 buffers are ~46 MB/min — twice the channels, four bytes
 * per sample). 10 minutes is a comfortable upper bound for synth
 * jamming; a forgotten "record" stops itself before it eats RAM.
 */
const MAX_DURATION_SEC = 600;

/**
 * Peak meter decay rate. The level returned by `getPeakLevel()` falls
 * by this much per second when no sample exceeds the current hold —
 * fast enough to feel responsive, slow enough that brief silences
 * don't make the indicator flicker. Tuned by feel.
 */
const PEAK_DECAY_PER_SEC = 1.6;

export type RecorderState = 'idle' | 'recording';

export type RecorderListener = (state: RecorderState) => void;

/** Reason a recording ended — manual stop vs the safety auto-stop. */
export type RecorderStopReason = 'user' | 'maxDuration';

export type RecorderStopListener = (
  reason: RecorderStopReason,
  result: RecordingResult,
) => void;

/**
 * Capture-side ScriptProcessorNode shape — typed loosely so we don't
 * lean on the deprecated `ScriptProcessorNode` global being present
 * in every TS lib config.
 */
interface ScriptTapNode extends AudioNode {
  onaudioprocess: ((e: AudioProcessingEvent) => void) | null;
}

/**
 * Record/stop result. Caller decides whether to download the blob,
 * stash it for later, or both.
 */
export interface RecordingResult {
  readonly blob: Blob;
  /** Human-readable filename suggestion. Includes the active preset
   *  name when one was supplied to `start()`:
   *  `evil-synth-glass-bells-20260424-153012.wav`. Falls back to the
   *  bare timestamp form when no preset name is in play. */
  readonly suggestedName: string;
  /** Length in seconds, useful for UI readouts. */
  readonly durationSec: number;
  /** Why the recording ended — manual stop vs auto-stop ceiling. */
  readonly reason: RecorderStopReason;
}

/** Optional config for `start()`. */
export interface RecorderStartOptions {
  /** Currently-loaded patch name, used to build a friendlier WAV
   *  filename. Slugified into the suggested filename so DAWs and
   *  finder windows display something meaningful instead of a row of
   *  identical timestamps. */
  readonly presetName?: string;
}

export class Recorder {
  private engine: AudioEngine | null = null;
  private state: RecorderState = 'idle';

  private tap: GainNode | null = null;
  private script: ScriptTapNode | null = null;
  private silentSink: GainNode | null = null;
  /** Captured chunks per channel. Concat once on stop. */
  private channels: [Float32Array[], Float32Array[]] = [[], []];
  private capturedSamples = 0;
  /** Active preset name supplied to `start()`. Used to build a
   *  friendlier suggested filename on stop. */
  private presetName: string | null = null;
  /** Running peak level (max |sample|) over the current hold window.
   *  Decays toward zero between callbacks via getPeakLevel(). */
  private peakLevel = 0;
  private peakUpdatedAt = 0;

  private readonly listeners = new Set<RecorderListener>();
  private readonly stopListeners = new Set<RecorderStopListener>();

  /** Bind once — Recorder is engine-scoped. */
  attach(engine: AudioEngine): void {
    this.engine = engine;
  }

  getState(): RecorderState {
    return this.state;
  }

  subscribe(fn: RecorderListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Subscribe to stop events. Fires AFTER the state-change listeners,
   * with the final result and the reason — manual user stop vs the
   * auto-stop safety cap. Lets the UI distinguish "the user pressed
   * Stop" from "the recorder ended itself" so we can surface a toast
   * for the latter.
   */
  onStop(fn: RecorderStopListener): () => void {
    this.stopListeners.add(fn);
    return () => {
      this.stopListeners.delete(fn);
    };
  }

  /**
   * Length of the in-flight recording in seconds. Returns 0 when idle
   * (no buffers captured yet) — same value the UI would show on first
   * frame, so callers don't need a guard.
   */
  getCurrentDurationSec(): number {
    if (this.state !== 'recording' || !this.engine) return 0;
    return this.capturedSamples / this.engine.context.sampleRate;
  }

  /**
   * Current peak amplitude for the meter UI. Decays toward zero between
   * audio callbacks so a held loud passage doesn't pin the indicator
   * forever — the level returned reflects what just happened plus a
   * quick smear so transients are visible. 0..1+ (clipping shows as
   * >=1.0).
   */
  getPeakLevel(): number {
    if (this.state !== 'recording') return 0;
    // Decay applied per-call so the indicator drops smoothly when the
    // signal goes quiet. Wall-clock dt rather than sample-accurate;
    // the meter is cosmetic, not the recording itself.
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const dt = Math.max(0, (now - this.peakUpdatedAt) / 1000);
    this.peakLevel = Math.max(0, this.peakLevel - PEAK_DECAY_PER_SEC * dt);
    this.peakUpdatedAt = now;
    return this.peakLevel;
  }

  /**
   * Begin recording. No-op if already recording or if the engine
   * isn't attached yet (the UI guards on getState() so this should
   * be rare in practice). `opts.presetName` flows through to the
   * suggested filename on stop.
   */
  start(opts: RecorderStartOptions = {}): void {
    if (this.state === 'recording') return;
    const engine = this.engine;
    if (!engine) return;

    const ctx = engine.context;
    // ScriptProcessorNode is deprecated but still ubiquitous; cast
    // through unknown so we don't depend on the optional DOM lib type.
    const ctxAny = ctx as unknown as {
      createScriptProcessor(
        bufferSize: number,
        numberOfInputChannels: number,
        numberOfOutputChannels: number,
      ): ScriptTapNode;
    };
    if (typeof ctxAny.createScriptProcessor !== 'function') {
      console.warn('[recorder] ScriptProcessorNode is unavailable; recording disabled');
      return;
    }

    this.channels = [[], []];
    this.capturedSamples = 0;
    this.presetName = opts.presetName?.trim() || null;
    this.peakLevel = 0;
    this.peakUpdatedAt =
      typeof performance !== 'undefined' ? performance.now() : Date.now();

    this.tap = ctx.createGain();
    this.tap.gain.value = 1;
    engine.master.connect(this.tap);

    this.script = ctxAny.createScriptProcessor(TAP_BUFFER_SIZE, 2, 2);
    const sampleRate = ctx.sampleRate;
    const maxSamples = Math.floor(MAX_DURATION_SEC * sampleRate);
    this.script.onaudioprocess = (e: AudioProcessingEvent): void => {
      // Defensive — the script can fire one final callback after we've
      // started teardown but before the disconnect lands. Drop those.
      if (this.state !== 'recording') return;
      const inputBuf = e.inputBuffer;
      // Stereo expected; mono inputs get duplicated to both channels
      // so the WAV is always two-channel.
      const l = inputBuf.getChannelData(0);
      const r =
        inputBuf.numberOfChannels > 1 ? inputBuf.getChannelData(1) : l;
      // Copy because the input buffer is reused across callbacks.
      this.channels[0].push(new Float32Array(l));
      this.channels[1].push(new Float32Array(r));
      this.capturedSamples += l.length;
      // Peak tracking — scan both channels for the loudest sample in
      // this batch. Latches the new max if higher than the decayed
      // value; getPeakLevel() drops it back down between callbacks.
      let batchPeak = 0;
      for (let i = 0; i < l.length; i++) {
        const a = Math.abs(l[i] ?? 0);
        const b = Math.abs(r[i] ?? 0);
        if (a > batchPeak) batchPeak = a;
        if (b > batchPeak) batchPeak = b;
      }
      if (batchPeak > this.peakLevel) {
        this.peakLevel = batchPeak;
        this.peakUpdatedAt =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
      }
      // Auto-stop ceiling — schedule on the next microtask so the
      // current callback completes cleanly before we disconnect the
      // node it's running on. Mark state synchronously so any further
      // callbacks bail out at the guard above.
      if (this.capturedSamples >= maxSamples) {
        this.state = 'idle';
        // setState would fire listeners — defer along with stop() so
        // 'idle' only goes out once, with the result alongside.
        Promise.resolve().then(() => this.finalizeStop('maxDuration'));
      }
    };

    this.tap.connect(this.script);

    // Silent sink keeps the script alive without re-routing audio
    // back to the speakers.
    this.silentSink = ctx.createGain();
    this.silentSink.gain.value = 0;
    this.script.connect(this.silentSink);
    this.silentSink.connect(ctx.destination);

    this.setState('recording');
  }

  /**
   * Stop recording, encode WAV, return the blob + a suggested filename.
   * Returns null if no recording was in progress (defensive — UI
   * should guard, but a misuse shouldn't blow up). Pairs with
   * `onStop` listeners so callers that prefer the event flow can
   * just listen instead.
   */
  stop(): RecordingResult | null {
    if (this.state !== 'recording' && !this.tap) return null;
    return this.finalizeStop('user');
  }

  /**
   * Shared teardown + result production for both the user-initiated
   * stop() and the auto-stop ceiling. Idempotent — if the tap graph
   * is already torn down, returns null.
   */
  private finalizeStop(reason: RecorderStopReason): RecordingResult | null {
    const engine = this.engine;
    if (!engine) return null;
    if (!this.tap && !this.script) return null;
    const ctx = engine.context;

    // Tear down the tap graph first so no extra samples land after
    // we've snapshotted the buffers.
    try {
      if (this.tap) {
        engine.master.disconnect(this.tap);
        this.tap.disconnect();
      }
      if (this.script) {
        this.script.onaudioprocess = null;
        this.script.disconnect();
      }
      if (this.silentSink) this.silentSink.disconnect();
    } catch {
      /* nodes may have already been disposed; ignore */
    }
    this.tap = null;
    this.script = null;
    this.silentSink = null;

    const totalSamples = this.capturedSamples;
    const sampleRate = ctx.sampleRate;
    const left = flatten(this.channels[0], totalSamples);
    const right = flatten(this.channels[1], totalSamples);
    this.channels = [[], []];

    const blob = encodeWav(left, right, sampleRate);
    const durationSec = totalSamples / sampleRate;
    const suggestedName = makeFilename(this.presetName);
    const result: RecordingResult = { blob, suggestedName, durationSec, reason };

    // State may already be 'idle' if the auto-stop path beat us here;
    // setState no-ops on equal state so the listener fires exactly
    // once regardless of which entry point triggered teardown.
    this.setState('idle');
    for (const fn of this.stopListeners) fn(reason, result);
    this.presetName = null;
    this.peakLevel = 0;
    return result;
  }

  /**
   * Trigger a browser download for a previously-captured blob. Lives
   * here so the UI doesn't have to re-implement the
   * "create-anchor-click-revoke" dance every time.
   */
  static download(result: RecordingResult): void {
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke a tick so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private setState(state: RecorderState): void {
    if (this.state === state) return;
    this.state = state;
    for (const fn of this.listeners) fn(state);
  }
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Concatenate a list of Float32Array chunks into one buffer of the
 * exact target length. Pads with silence if chunks fall short (which
 * shouldn't happen, but a defensive default beats undefined samples).
 */
function flatten(chunks: readonly Float32Array[], total: number): Float32Array {
  const out = new Float32Array(total);
  let off = 0;
  for (const c of chunks) {
    if (off + c.length > total) {
      out.set(c.subarray(0, total - off), off);
      return out;
    }
    out.set(c, off);
    off += c.length;
  }
  return out;
}

/**
 * Encode interleaved 16-bit PCM stereo WAV. Format reference:
 * http://soundfile.sapp.org/doc/WaveFormat/
 *
 * Float32 → Int16 conversion clamps to [-1, +1] and scales by 0x7FFF /
 * 0x8000 for positive / negative half so the full int16 range is used
 * without offset distortion at zero crossing.
 */
function encodeWav(
  left: Float32Array,
  right: Float32Array,
  sampleRate: number,
): Blob {
  const length = left.length;
  const bytesPerSample = 2;
  const numChannels = 2;
  const dataSize = length * numChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');

  // fmt chunk (PCM)
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true); // byte rate
  view.setUint16(32, numChannels * bytesPerSample, true); // block align
  view.setUint16(34, bytesPerSample * 8, true); // bits per sample

  // data chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Interleave L/R, convert Float32 → Int16.
  let offset = 44;
  for (let i = 0; i < length; i++) {
    const l = clamp(left[i] ?? 0);
    const r = clamp(right[i] ?? 0);
    view.setInt16(offset, floatToInt16(l), true);
    view.setInt16(offset + 2, floatToInt16(r), true);
    offset += 4;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function clamp(v: number): number {
  return Math.max(-1, Math.min(1, v));
}

function floatToInt16(v: number): number {
  return v < 0 ? Math.round(v * 0x8000) : Math.round(v * 0x7fff);
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * Suggested filename. Format:
 *   `evil-synth-<slug>-YYYYMMDD-HHMMSS.wav`  when a preset name is in
 *   play, where <slug> is the slugified preset name; or
 *   `evil-synth-YYYYMMDD-HHMMSS.wav`         otherwise.
 *
 * Local-time stamp so multiple recordings in a session don't collide
 * and the user can find them in their Downloads folder by date. Slug
 * is capped at 32 chars so wild preset names don't generate
 * pathological filenames.
 */
function makeFilename(presetName: string | null): string {
  const now = new Date();
  const pad = (n: number, width = 2): string => String(n).padStart(width, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const slug = presetName ? slugify(presetName) : '';
  return slug
    ? `evil-synth-${slug}-${stamp}.wav`
    : `evil-synth-${stamp}.wav`;
}

/**
 * Slugify a preset name for filename use:
 *   - lowercase, strip diacritics
 *   - replace any run of non-alphanumeric with a single hyphen
 *   - trim leading / trailing hyphens
 *   - cap length at 32 chars to keep filenames sane
 */
function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/g, '');
}
